// Keep this module identical to backend/landingpage/src/privacy/consent.ts.
// Each app builds independently; the privacy test checks that they stay in sync.
import * as Consent from 'vanilla-cookieconsent'
import 'vanilla-cookieconsent/dist/cookieconsent.css'
import './privacy.css'

let started: Promise<void> | undefined
let privacyChannel: BroadcastChannel | undefined
export const hasGpc = () =>
  (navigator as Navigator & { globalPrivacyControl?: boolean })
    .globalPrivacyControl === true
export const allowed = (category: 'analytics') => {
  const cookie = Consent.getCookie()
  return (
    !hasGpc() &&
    Consent.validConsent() &&
    cookie?.revision === 1 &&
    cookie.categories?.includes(category) === true
  )
}
export const analyticsAllowed = () =>
  allowed('analytics') &&
  !/(reset-password|verify-email|oidc\/callback)/.test(location.pathname) &&
  !Array.from(new URLSearchParams(location.search).keys()).some((key) =>
    /token|code|password|secret|email/i.test(key),
  )
export const safeAnalyticsUrl = (value: string) => {
  try {
    const url = new URL(value, location.origin)
    return value ? url.origin + url.pathname : ''
  } catch {
    return ''
  }
}
export const showPrivacyPreferences = () => {
  const cookie = Consent.getCookie()
  if (
    cookie?.revision === 1 &&
    Array.isArray(cookie.categories) &&
    ['analytics'].some(
      (category) =>
        Consent.acceptedCategory(category) !==
        cookie.categories.includes(category),
    )
  ) {
    Consent.acceptCategory(
      cookie.categories.filter((category) =>
        ['necessary', 'analytics'].includes(category),
      ),
    )
  }
  Consent.showPreferences()
}

function clearAnalyticsStorage() {
  for (const storage of [localStorage, sessionStorage]) {
    for (const key of Object.keys(storage)) {
      if (key.startsWith('ph_')) storage.removeItem(key)
    }
  }
  // Also clear legacy cookies created before this consent implementation.
  for (const entry of document.cookie.split(';')) {
    const name = entry.trim().split('=')[0]
    if (!/^(_ga($|_)|_gid$|_gat|ph_)/.test(name)) continue
    const host = location.hostname
    const domains = [
      '',
      host,
      ...(host.endsWith('.getyogen.com') || host === 'getyogen.com'
        ? ['getyogen.com']
        : []),
    ]
    for (const domain of domains) {
      document.cookie = `${name}=; Max-Age=0; Path=/;${domain ? ` Domain=${domain};` : ''} SameSite=Lax`
    }
  }
}
function update() {
  window.dispatchEvent(new Event('yogen:privacy-change'))
  if (!allowed('analytics')) {
    try {
      clearAnalyticsStorage()
    } catch {
      /* Storage may be disabled. Tracking remains off. */
    }
  }
}
export function startPrivacy() {
  if (started) return started
  if ('BroadcastChannel' in window) {
    privacyChannel = new BroadcastChannel('yogen-privacy')
    privacyChannel.onmessage = () => {
      update()
    }
  }
  window.addEventListener('focus', update)
  // Astro replaces the body during client navigation. Keep the live consent
  // controls (including their handlers), not just their saved cookie.
  document.addEventListener('astro:before-swap', (event) => {
    const nextDocument = (event as Event & { newDocument?: Document })
      .newDocument
    if (!nextDocument) return
    for (const selector of ['#cc-main', '#yogen-privacy-link']) {
      const node = document.querySelector(selector)
      if (node) nextDocument.body.append(node)
    }
  })
  started = Consent.run({
    cookie: {
      name: 'yogen_privacy',
      path: '/',
      expiresAfterDays: 182,
      sameSite: 'Lax',
    },
    revision: 1,
    mode: 'opt-in',
    hideFromBots: false,
    disablePageInteraction: false,
    guiOptions: {
      consentModal: {
        layout: 'box inline',
        position: 'bottom right',
        equalWeightButtons: true,
      },
      preferencesModal: { layout: 'box', equalWeightButtons: true },
    },
    categories: {
      necessary: { enabled: true, readOnly: true },
      analytics: { enabled: false },
    },
    language: {
      default: 'en',
      translations: {
        en: {
          consentModal: {
            title: 'Your privacy choices',
            description:
              'Yogen works with or without optional analytics. Allow usage analytics to help us improve it. No session recording.' +
              (hasGpc()
                ? ' Your browser’s Global Privacy Control keeps analytics off.'
                : ''),
            acceptAllBtn: 'Accept optional',
            acceptNecessaryBtn: 'Reject optional',
            showPreferencesBtn: 'Manage preferences',
            footer:
              '<a href="https://getyogen.com/privacy-policy.html">Privacy policy</a> · <a href="https://getyogen.com/cookie-policy.html">Cookie details</a>',
          },
          preferencesModal: {
            title: 'Privacy preferences',
            acceptAllBtn: 'Accept optional',
            acceptNecessaryBtn: 'Reject optional',
            savePreferencesBtn: 'Save preferences',
            closeIconLabel: 'Close preferences',
            sections: [
              {
                title: 'Your choice, anytime',
                description:
                  'These choices apply to this browser across Yogen’s website and app, not to your whole organization. Rejecting optional services does not affect login, hiring features, or checkout. You can change your choice anytime. Choices are remembered for six months.' +
                  (hasGpc()
                    ? ' Global Privacy Control is enabled in your browser, so analytics remains off.'
                    : ''),
              },
              {
                title: 'Essential services',
                description:
                  'Keep Yogen working, including sign-in, security, AI features, saved work, PDFs, checkout and requested product functionality. These remain available when you reject optional analytics. Sentry provides limited, scrubbed error reporting even when optional analytics are off. Sentry session replay and performance tracing are disabled.',
                linkedCategory: 'necessary',
              },
              {
                title: 'Usage analytics',
                description:
                  'Google Analytics on the website and PostHog analytics help us understand page and feature usage. We do not send your name or email as analytics profile fields. Session recording is disabled.',
                linkedCategory: 'analytics',
              },
              {
                title: 'Privacy requests',
                description:
                  'To ask about access, correction or deletion of personal data, contact <a href="mailto:help@getyogen.com">help@getyogen.com</a>. Rejecting analytics stops future Google Analytics and PostHog collection in this browser and clears matching first-party analytics cookies and PostHog browser storage. It does not delete data already received by providers, saved work, or your account. Contact us to request deletion of previously collected personal data.',
              },
            ],
          },
        },
      },
    },
    onConsent: () => {
      update()
      privacyChannel?.postMessage('changed')
    },
    onChange: () => {
      update()
      privacyChannel?.postMessage('changed')
    },
  }).then(() => {
    document
      .querySelector('#cc-main')
      ?.setAttribute('data-astro-transition-persist', 'yogen-consent')
    update()
    document.addEventListener('click', (event) => {
      if ((event.target as Element)?.closest('[data-yogen-privacy]'))
        showPrivacyPreferences()
    })
    // A permanent entry point also covers public login and documentation pages.
    if (!document.querySelector('#yogen-privacy-link')) {
      const button = document.createElement('button')
      button.id = 'yogen-privacy-link'
      button.setAttribute('data-astro-transition-persist', 'yogen-privacy-link')
      button.textContent = 'Privacy preferences'
      button.dataset.yogenPrivacy = ''
      document.body.append(button)
    }
  })
  return started
}

export function openProductDemo() {
  if (document.querySelector('#yogen-product-demo')) return
  const dialog = document.createElement('dialog')
  dialog.id = 'yogen-product-demo'
  dialog.setAttribute('aria-label', 'Yogen product walkthrough')
  const close = document.createElement('button')
  close.textContent = 'Close walkthrough'
  close.onclick = () => dialog.close()
  const frame = document.createElement('iframe')
  frame.title = 'Yogen product walkthrough'
  // Workspace tracking is disabled in Supademo. Never send its analytics
  // consent postMessage: opening a requested demo is not analytics consent.
  frame.src = 'https://app.supademo.com/embed/cmlbio3ga00qx0z0i406i4311'
  dialog.append(close, frame)
  dialog.addEventListener('close', () => dialog.remove())
  document.body.append(dialog)
  dialog.showModal()
}
